import { useId, useState, type ReactNode } from "react";
import type { AppState } from "../../api";
import { money, when } from "../../money/types";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Field, Notice } from "../ui/workflow";
import { QueryState, useSnapshot } from "../commerce/shared";
import "./sales-report.css";

type Props = {
  user: AppState;
  onOpenBag?: (id: string) => void;
  onNavigate?: (section: string, tab?: string, objectId?: string) => void;
};
type SaleRow = {
  id: number;
  kind: "sale" | "return" | "correction";
  product_id?: string;
  preorder_id?: string | null;
  sku: string;
  title: string;
  at: string;
  platform: string;
  revenue: number;
  purchase: number;
  delivery: number;
  other: number;
  profit: number;
  kolya: number;
  vlad: number;
  warning?: string | null;
};
type PendingSale = Pick<SaleRow, "product_id" | "preorder_id" | "sku" | "title" | "at" | "platform"> & {
  sale_id: string;
  gross_amount: number;
};
export type SalesReportData = {
  from: string;
  to: string;
  sale_type: "ordinary" | "preorder";
  expenses_excluded: boolean;
  totals: { sales: number; returns: number; revenue: number; profit: number; expenses: number; net: number; kolya: number; vlad: number };
  rows: SaleRow[];
  pending_sales: PendingSale[];
  pending_count: number;
  pending_total: number;
  warnings?: string[];
};

const platforms: Record<string, string> = { telegram: "Telegram", avito: "Авито", oskelly: "Oskelly", direct: "Частник" };
const signedMoney = (value: number | undefined) => value == null ? "—" : `${value > 0 ? "+" : value < 0 ? "−" : ""}${money(Math.abs(value))}`;

function ReportFact({ label, children }: { label: string; children: ReactNode }) {
  return <div className="sales-report-fact"><span>{label}</span><strong>{children}</strong></div>;
}

function OwnerShares({ kolya, vlad, explain = false }: { kolya: number; vlad: number; explain?: boolean }) {
  const [helpOpen, setHelpOpen] = useState(false);
  const helpId = useId();
  return <div className="sales-report-shares">
    <div className="sales-report-shares-heading"><h3>Доли результата</h3>{explain && <Button
      type="button" variant="outline" className="sales-report-help" aria-label="Что означают доли"
      aria-expanded={helpOpen} aria-controls={helpId} onClick={() => setHelpOpen(!helpOpen)}
      onKeyDown={event => { if (event.key === "Escape") setHelpOpen(false); }}
    >?</Button>}</div>
    {explain && <p id={helpId} hidden={!helpOpen} className="sales-report-help-text">Доли результата за период. Это не остатки счетов и не суммы к выводу.</p>}
    <div className="sales-report-owners"><div><span>Коля</span><strong>{signedMoney(kolya)}</strong></div><div><span>Влад</span><strong>{signedMoney(vlad)}</strong></div></div>
  </div>;
}

function OpenProduct({ row, onOpenBag, onNavigate, pending = false }: Pick<Props, "onOpenBag" | "onNavigate"> & { row: Pick<SaleRow, "product_id" | "preorder_id">; pending?: boolean }) {
  if (row.preorder_id && onNavigate) return <Button variant="outline" onClick={() => onNavigate("preorders", pending ? "active" : "history", row.preorder_id!)}>Открыть предзаказ</Button>;
  if (row.product_id && onOpenBag) return <Button variant="outline" onClick={() => onOpenBag(row.product_id!)}>Открыть сумку</Button>;
  return null;
}

function ReportContents({ data, user, onOpenBag, onNavigate }: Props & { data: SalesReportData }) {
  const t = data.totals;
  const preorder = data.sale_type === "preorder";
  const excluded = data.expenses_excluded;
  // A period with only expenses (including their reversals) still has a result.
  const showSummary = data.rows.length > 0 || Object.values(t).some(value => value !== 0);
  const rows = [...data.rows].sort((a, b) => Date.parse(b.at) - Date.parse(a.at) || b.id - a.id);
  const corrections = rows.filter(row => row.kind === "correction").length;
  const historical = t.returns > 0 || corrections > 0;
  return <>
    {showSummary && <section className="sales-report-summary" aria-label="Итог периода">
      <h2>{excluded ? "Результат продаж" : "После общих расходов"}</h2>
      <p className="sales-report-total">{signedMoney(excluded ? t.profit : t.net)}</p>
      {excluded && <p className="sales-report-excluded">Общие расходы не учтены</p>}
      <div className="sales-report-summary-meta"><span>{preorder ? "Передано" : "Продаж"}: {t.sales}</span><span>Получено <strong>{money(t.revenue)}</strong></span></div>
      {historical && <p className="sales-report-event-counts">{[t.returns > 0 && `Возвратов: ${t.returns}`, corrections > 0 && `Корректировок: ${corrections}`].filter(Boolean).join(" · ")}</p>}
      {!excluded && <div className="sales-report-calculation"><ReportFact label="Результат продаж">{signedMoney(t.profit)}</ReportFact><ReportFact label="Общие расходы">{signedMoney(-t.expenses)}</ReportFact></div>}
      <OwnerShares kolya={t.kolya} vlad={t.vlad} explain />
    </section>}

    {data.pending_count > 0 && <details className="sales-report-disclosure">
      <summary><span>Ждут расчёта · {data.pending_count}</span><strong>{money(data.pending_total)}</strong></summary>
      <p className="sales-report-note">Оплаты, ожидающие приёмки. В результат не входят.</p>
      {data.pending_sales.map(row => <div key={row.sale_id} className="sales-report-pending">
        <ReportFact label={`${row.sku} · ${row.title}`}>{money(row.gross_amount)}</ReportFact>
        <p>{when(row.at, user.timezone)}</p><OpenProduct row={row} onOpenBag={onOpenBag} onNavigate={onNavigate} pending />
      </div>)}
    </details>}

    {Boolean(data.warnings?.length) && <details className="sales-report-disclosure"><summary>Замечания по учёту · {data.warnings!.length}</summary><p className="sales-report-note">Могут относиться к другим периодам.</p>{data.warnings!.map(warning => <p className="sales-report-note" key={warning}>{warning}</p>)}</details>}

    {rows.length > 0 ? <details className="sales-report-disclosure sales-report-events">
      <summary>{historical ? "События" : preorder ? "Передачи" : "Продажи"} · {rows.length}</summary>
      {rows.map(row => <details className="sales-report-event" key={row.id}>
        <summary><span className="sales-report-event-title">
          <span>{row.kind === "return" ? "Возврат · " : row.kind === "correction" ? "Корректировка · " : ""}{row.title || row.sku}</span>
          <strong>{signedMoney(row.profit)}</strong>
          <span className="sales-report-event-meta">{row.sku} · {when(row.at, user.timezone)}{!preorder && ` · ${platforms[row.platform] || row.platform}`}</span>
        </span></summary>
        <div className="sales-report-event-details">
          <ReportFact label={row.kind === "sale" ? "Получено" : "Изменение выручки"}>{money(row.revenue)}</ReportFact>
          <ReportFact label="Выкуп">{money(row.purchase)}</ReportFact>
          <ReportFact label="Доставка">{money(row.delivery)}</ReportFact>
          <ReportFact label="Прочее">{money(row.other)}</ReportFact>
          <ReportFact label="Результат">{signedMoney(row.profit)}</ReportFact>
          <OwnerShares kolya={row.kolya} vlad={row.vlad} />
          {row.warning && <Notice>{row.warning}</Notice>}
          <OpenProduct row={row} onOpenBag={onOpenBag} onNavigate={onNavigate} />
        </div>
      </details>)}
    </details> : <p className="sales-report-empty">{data.pending_count > 0 ? "Проведённых продаж нет" : "За этот период продаж нет"}</p>}
    {!showSummary && excluded && <p className="sales-report-note">Общие расходы не учтены</p>}
  </>;
}

export function SalesReport(props: Props) {
  const { user } = props;
  const [period, setPeriod] = useState("month");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [kind, setKind] = useState("ordinary");
  const [platform, setPlatform] = useState("");
  const invalid = period === "custom" && (!from || !to || from > to);
  const params = new URLSearchParams({ period, sale_type: kind, platform: kind === "preorder" ? "" : platform, ...(period === "custom" ? { from, to } : {}) }).toString();
  const report = useSnapshot<SalesReportData>(`reports?${params}`, user.can_finance && !invalid);
  if (!user.can_finance) return <Notice>Этот раздел доступен владельцам.</Notice>;
  return <section className="sales-report" aria-label="Итоги продаж">
    <div className="sales-report-kind" role="group" aria-label="Вид продаж">
      {[{ value: "ordinary", label: "Обычные" }, { value: "preorder", label: "Предзаказы" }].map(option => <Button key={option.value} type="button" variant={kind === option.value ? "default" : "outline"} aria-pressed={kind === option.value} onClick={() => setKind(option.value)}>{option.label}</Button>)}
    </div>
    <div className={`sales-report-filters${kind === "preorder" ? " sales-report-filters-single" : ""}`}>
      <select className="next-control" aria-label="Период" value={period} onChange={event => setPeriod(event.target.value)}>
        <option value="month">Этот месяц</option><option value="week">Эта неделя</option><option value="year">Этот год</option><option value="all">Всё время</option><option value="custom">Свои даты</option>
      </select>
      {kind === "ordinary" && <select className="next-control" aria-label="Площадка" value={platform} onChange={event => setPlatform(event.target.value)}>
        <option value="">Все площадки</option>{Object.entries(platforms).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select>}
    </div>
    {period === "custom" && <div className="sales-report-dates"><Field label="С даты"><Input type="date" value={from} onChange={event => setFrom(event.target.value)} /></Field><Field label="По дату"><Input type="date" value={to} min={from} onChange={event => setTo(event.target.value)} /></Field></div>}
    {invalid ? <Notice>{from && to ? "Начало позже конца периода." : "Выберите обе даты."}</Notice> : <QueryState query={report}>
      {report.data && <ReportContents key={params} data={report.data} {...props} />}
    </QueryState>}
  </section>;
}
